import { FC } from "react";
import { IBusinessElement } from "../../../../../redux/businesses";
import { useDispatch, useSelector } from "../../../../../hooks";
import { upgradeBusinessesElement } from "../../../../../redux/businesses/upgrade-business-element.handler";
import { setProfit, setUserExp } from "../../../../../redux/user";
import { BusinessElementState, BusinessElementStateType } from "./BusinessElementState/BusinessElementState";
import { BalanceErrorType, useBalanceError } from "../../../../../hooks/error";
import { setUserBalanceAndLog } from "../../../../../redux/user/set-user-balance-and-log.handler";
import { BusinessCard } from "../../BusinessCard/BusinessCard";
import styles from "./BusinessElementCard.module.scss";
import { BusinessElementStateCompleted } from "./BusinessElementStateCompleted/BusinessElementStateCompleted";
import { BuyButton } from "../../../../../components/Buttons/BuyButton/BuyButton";
import { ActionPromptType } from "../../../../../hooks/prompts";

interface IBusinessElementCardProps extends IBusinessElement {
    image: string;
}

export const BusinessElementCard: FC<IBusinessElementCardProps> = ({ type, name, level, cost, current, next, dependency, isAllowUpgrade, image }) => {
    const dispatch = useDispatch();
    const business = useSelector(store => store.businesses.activeBusiness);
    const balance = useSelector(store => store.user.info.balance);
    const balanceError = useBalanceError();

    if (!business)
        return <></>;

    const isUpgradable = () => cost > 0;
    const isAreaLimited = () => next ? next.area > business.area.currentValue : false;
    const isStaffLimited = () => next ? next.staff > business.staff.currentValue : false;
    const isDependencySatisfied = () => !dependency || isAllowUpgrade === true;
    const isButtonAllow = () => !isAreaLimited() && !isStaffLimited() && isDependencySatisfied();

    const onUpgradeHandler = async () => {
        if (!isUpgradable() || !business)
            return;

        if (balance < cost) {
            balanceError(cost, BalanceErrorType.BusinessElementUpgrade);
            return;
        }

        const resultAction = await dispatch(upgradeBusinessesElement({
            businessType: business.type,
            elementType: type
        }));
        
        if (upgradeBusinessesElement.fulfilled.match(resultAction)) {            
            const { balance, exp, profit } = resultAction.payload;
            if (balance !== undefined)
                dispatch(setUserBalanceAndLog(balance));

            if (exp !== undefined)
                dispatch(setUserExp(exp));

            if (profit !== undefined)
                dispatch(setProfit(profit));
        }
    }
    
    return (
        <BusinessCard>
            <div className={styles.businessElement}>
                <div className={styles.imageContainer}>
                    <img src={image} alt={name} className={styles.backgroundImage} />
                    <div className={styles.title}>{name}</div>
                </div>

                <div className={styles.states}>
                    <BusinessElementState
                        type={ BusinessElementStateType.Current }
                        elementType= { type }
                        level={ level }
                        profit={ current.profit }
                        area={ current.area }
                        staff={ current.staff }
                    />

                    { isUpgradable()
                        ? <BusinessElementState
                            type={ isUpgradable() ? BusinessElementStateType.Next : BusinessElementStateType.Max }
                            elementType= { type }
                            level={ level + 1 }
                            profit={ next?.profit }
                            area={ next?.area }
                            areaLimited={ isAreaLimited() }
                            staff={ next?.staff }
                            staffLimited={ isStaffLimited() }
                            isAllowUpgrade={ isButtonAllow() }
                            dependency={ dependency }
                        />
                        : <BusinessElementStateCompleted />
                    }
                </div>

                { isButtonAllow() && isUpgradable() &&
                    <BuyButton
                        cost={ cost }
                        caption={'Улучшить за'}
                        handler={ onUpgradeHandler }
                        promptType={ ActionPromptType.UPGRADE_BUSINESS_FIRST_TIME }
                    />
                }
            </div>
        </BusinessCard>
    );
}